import { useEffect, useRef } from "react";
import "./ListingHeader.scss";
import { merchandisingShape, productShape, resolutionPropType } from "app/utils/propTypes";
import PropTypes from "prop-types";
import Jumbotron from "app/pages/Listing/Product/Jumbotron";
import ListingActionbar from "app/pages/.shared/ListingActionbar/ListingActionbar";
import ListingHero from "@brand/Listing/ListingHero/ListingHero";
import NavLogsContainer from "app/pages/Listing/NavLogs/NavLogsContainer";
import SmartDPImmersiveSearch from "app/pages/SmartDP/Search/SmartDPImmersiveSearch/SmartDPImmersiveSearch";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import useOnScreen from "app/pages/.shared/useOnScreen";
import ReasonToBookBlockContainer from "app/pages/.shared/ReasonsToBook/ReasonToBookBlock/ReasonToBookBlockContainer";
import { useFsFlag } from "@flagship.io/react-sdk";
import PaymentDescriptionContainer from "app/pages/.shared/DynamicBlocks/blocks/PaymentDescriptionBlock/PaymentDescriptionContainer";

const ListingHeader = ({
	heroProduct,
	resolution,
	merchandisingHeader = [],
	products = [],
	listingHeaderRef,
	isNavlogsEnabled,
	// On affiche le nombre de produits dans le cas où il n'y a pas assez de produits à cause des filtres
	// On n'affiche pas le compteur à 4 (SSR qui ne récupère que les 4 premier produits)
	displayProductsCount,
	partnerCode,
	marketingBanner,
	showPfsMessage,
	partnerListingHero = {},
	isPaymentDescriptionBlockExist,
}) => {
	const { track } = useAnalytics();
	const ref = useRef();
	const isElementOnScreen = useOnScreen({ ref, triggerOnce: false });

	const merch = merchandisingHeader[0];

	const flag = useFsFlag("marketing_bandeau_facilite_paiement");
	const flagValue = flag.getValue(false);

	useEffect(() => {
		if (isElementOnScreen && merch?.code) {
			track("merch_view", {
				label: merch.label,
				code: merch.code,
				position: String(merch.position),
				merchCode: merch.code,
			});
		}
	}, [isElementOnScreen, merch?.code]);

	const {
		heroTitleMobile,
		heroTitleDesktop,
		heroSubtitleMobile,
		heroSubtitleDesktop,
		heroImageMobile,
		heroImageDesktop,
		heroImageMobileThumbnail,
		heroImageDesktopThumbnail,
		displaySmartDPForm = false,
		showSDPSearchEngine = false,
	} = partnerListingHero;

	let header = "";
	if (heroProduct) {
		header = (
			<div className="listing-header__hero-product" data-testid="listing-header-hero-product">
				<Jumbotron product={heroProduct} />
			</div>
		);
	} else if (showSDPSearchEngine) {
		header = (
			<SmartDPImmersiveSearch
				heroTitleDesktop={heroTitleDesktop}
				heroTitleMobile={heroTitleMobile}
				heroSubtitleDesktop={heroSubtitleDesktop}
				heroSubtitleMobile={heroSubtitleMobile}
				heroTextAlign="left"
				heroImageMobile={heroImageMobile}
				heroImageDesktop={heroImageDesktop}
				heroImageMobileThumbnail={heroImageMobileThumbnail}
				heroImageDesktopThumbnail={heroImageDesktopThumbnail}
				hideSmartDPForm={!displaySmartDPForm}
			/>
		);
	} else {
		header = (
			<ListingHero
				marketingBanner={marketingBanner}
				merchList={merchandisingHeader}
				partnerCode={partnerCode}
				resolution={resolution}
			/>
		);
	}

	return (
		<div className="listing-header" ref={ref}>
			{header}

			<div className="listing-header__action-section">
				<div className="listing-header__reason-to-book">
					<ReasonToBookBlockContainer />
				</div>

				{isNavlogsEnabled && (
					<div className="listing-header__navlogs">
						<NavLogsContainer />
					</div>
				)}

				{flagValue && (
					<div className="listing-header__payment-description">
						<PaymentDescriptionContainer />
					</div>
				)}

				<div ref={listingHeaderRef}>
					<ListingActionbar
						count={displayProductsCount && products.length}
						showPfsMessage={showPfsMessage}
						hidePaymentDescriptionBlock={isPaymentDescriptionBlockExist && flagValue}
					/>
				</div>
			</div>
		</div>
	);
};

ListingHeader.propTypes = {
	heroProduct: productShape,
	resolution: resolutionPropType,
	merchandisingHeader: PropTypes.arrayOf(merchandisingShape),
	products: PropTypes.arrayOf(productShape),
	// en react.16 createRef renvoit un object dont la prop current est populate par l'élément du DOM (et non une fonction comme sur les anciennes versions)
	listingHeaderRef: PropTypes.object,
	partnerCode: PropTypes.string,
	isNavlogsEnabled: PropTypes.bool,
	displayProductsCount: PropTypes.bool,
	showPfsMessage: PropTypes.bool,
	isPaymentDescriptionBlockExist: PropTypes.object,
	marketingBanner: PropTypes.object,
	partnerListingHero: PropTypes.object,
};

export default ListingHeader;
